import { Box, Typography, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import FilePicker from "components/FilePicker";
import useConsent from "hooks/useConsent";
import { useSnackbar } from "hooks/useSnackbar";
import React from "react";
import {
  removeBotAndGhostTeams,
  uploadBulkTeams,
} from "services/botAndGhostTeamManagementService";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileSaver from "file-saver";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TextField from "@mui/material/TextField";

const useStyles = makeStyles((theme) => ({
  sectionContainer: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  actionCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
  },
  buttonGroup: {
    display: "flex",
    flexFlow: "row wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  purgeContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  ageInput: {
    width: 150,
  },
  uploadContainer: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-end",
  },
  uploadActions: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const BotGhostTeamManagement = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);
  const [fileDetails, setFileDetails] = React.useState({
    file: null,
    fileType: ".csv",
  });
  const [ghostTeamAge, setGhostTeamAge] = React.useState(null);

  const onSelectFile = (file: any) => {
    if (!file) {
      setFileDetails((st) => ({ ...st, file: null }));
      return;
    }
    setFileDetails((st) => ({ ...st, file }));
  };

  const {
    isOpenConsent,
    constentDetails,
    openConsent,
    closeConsent,
    updateDetails,
  } = useConsent();

  const _openConsent = async (type) => {
    updateDetails({ type });
    openConsent();
  };

  const clearTeams = async (type) => {
    closeConsent();
    setLoading(true);

    try {
      await removeBotAndGhostTeams(type);
      showSnackbar(`All ${type} teams cleared successfully`, "success");
    } catch (error) {
      console.error("Error clearing teams:", error);
      showSnackbar("Failed to clear teams", "error");
    } finally {
      setLoading(false);
      closeConsent();
    }
  };

  const purgeGhostTeams = async () => {
    closeConsent();
    setLoading(true);

    try {
      const response = await removeBotAndGhostTeams("Ghost", ghostTeamAge);
      showSnackbar(response.data.message, "success");
    } catch (error) {
      console.error("Error purging ghost teams:", error);
      showSnackbar(error.details ?? error.message ?? "Failed to purge ghost teams", "error");
    } finally {
      setLoading(false);
      closeConsent();
    }
  };

  const getBtnText = () => {
    return constentDetails.type === "Upload"
      ? "Upload Teams File"
      : constentDetails.type === "PurgeGhost"
        ? "Purge Ghost Teams"
        : `Clear All ${constentDetails.type} teams`;
  };

  const getMessageText = () => {
    return constentDetails.type === "Upload"
      ? "This operation will upload the Teams File and update/overwrite the teams"
      : constentDetails.type === "PurgeGhost"
        ? `Are you sure you want to purge ghost teams older than ${ghostTeamAge} days`
        : `This operation will delete all ${constentDetails.type} teams. Are you sure you want to proceed?`;
  };

  const saveAsExcelFile = (buffer, fileName) => {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";

    const data = new Blob([buffer], {
      type: EXCEL_TYPE,
    });

    const now = new Date();
    const pad = (n: any) => n.toString().padStart(2, "0");
    const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
      now.getDate()
    )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    FileSaver.saveAs(data, fileName + "_" + dateStr + EXCEL_EXTENSION);
  };

  const uploadCSV = async () => {
    try {
      setLoading(true);
      const response = await uploadBulkTeams(fileDetails.file);

      if (response?.data?.data?.mismatchData) {
        // Lazy load xlsx only when there's mismatch data to export
        const xlsx = await import("xlsx");

        const worksheet = xlsx.utils.json_to_sheet(
          response.data.data.mismatchData
        );
        const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
        const excelBuffer = xlsx.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        saveAsExcelFile(excelBuffer, "mismatch_bot_teams");
        throw new Error(response.message);
      }

      showSnackbar(response.message);
      if (!response.status) throw new Error(response?.message);
    } catch (error) {
      console.error(error);
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
      setFileDetails((st) => ({ ...st, file: null }));
      closeConsent();
    }
  };

  const downloadTemp = () => {
    try {
      const link = document.createElement("a");
      link.href = "team_template.csv";
      link.download = "team_template.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      showSnackbar(error.message || "Download failed", "error");
    }
  };

  return (
    <ContentShell title="Bot & Ghost Team Management" loading={loading}>
      <Box className={classes.sectionContainer}>
        <Paper className={classes.actionCard}>
          <Typography variant="subtitle1" gutterBottom>
            Clear All Teams
          </Typography>
          <Box className={classes.buttonGroup}>
            {["Bot", "Ghost"].map((type) => (
              <CustomButton
                key={type}
                startIcon={<RestartAltIcon />}
                onClick={() => _openConsent(type)}
              >
                {`Clear All ${type} Teams`}
              </CustomButton>
            ))}
          </Box>
        </Paper>

        <Paper className={classes.actionCard}>
          <Typography variant="subtitle1" gutterBottom>
            Purge Old Ghost Teams
          </Typography>
          <Box className={classes.purgeContainer}>
            <TextField
              className={classes.ageInput}
              label="Days older than"
              type="number"
              value={ghostTeamAge}
              onChange={(e) => setGhostTeamAge(parseInt(e.target.value))}
              variant="outlined"
              size="small"
            />
            <CustomButton
              startIcon={<RestartAltIcon />}
              onClick={() => {
                updateDetails({ type: "PurgeGhost" });
                openConsent();
              }}
            >
              Purge Ghost Teams
            </CustomButton>
          </Box>
        </Paper>
      </Box>

      <Box className={classes.sectionContainer}>
        <Paper className={classes.actionCard}>
          <Typography variant="subtitle1" gutterBottom>
            Upload Teams CSV
          </Typography>
          <Box className={classes.uploadContainer}>
            <FilePicker
              label="Select Teams CSV"
              name="teams"
              fileType={fileDetails.fileType}
              value={fileDetails.file}
              showPreview={false}
              onFileSelect={onSelectFile}
            />
          </Box>
          <Box className={classes.uploadActions}>
            <CustomButton
              disabled={!fileDetails.file}
              startIcon={<CloudUploadIcon />}
              onClick={() => _openConsent("Upload")}
            >
              Upload
            </CustomButton>
            <CustomButton
              startIcon={<ArrowDownwardIcon />}
              onClick={downloadTemp}
              variant="outlined"
            >
              Download Template
            </CustomButton>
          </Box>
        </Paper>
      </Box>

      <DeleteConsentDialog
        btnText={getBtnText()}
        message={getMessageText()}
        itemName={constentDetails.itemName}
        open={isOpenConsent}
        onClose={closeConsent}
        onConfirm={() =>
          constentDetails.type === "Upload"
            ? uploadCSV()
            : constentDetails.type === "PurgeGhost"
              ? purgeGhostTeams()
              : clearTeams(constentDetails?.type)
        }
      />
    </ContentShell>
  );
};

export default BotGhostTeamManagement;
